import moment from 'moment';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IAppStore } from '../../../../types/IAppStore';
import { WEEK_DAY_NUMBER_TO_STRING } from '../../../shared/lib/weekDay';
import { IRestaurantWorkTimeToday, UseGetBookingRequestRangeHookType } from '../model/types';

export const useGetBookingRequestRange: UseGetBookingRequestRangeHookType = (propDate) => {
  const restaurantWorkTimes = useSelector((state: IAppStore) => state.schedule.restaurantWorkTimes);
  return useMemo<{ from: number; till: number }>(() => {
    const date = moment(propDate);
    const startDay = date.clone().startOf('day');
    const nowWeekDay = WEEK_DAY_NUMBER_TO_STRING[date.day()];
    const restaurantWorkTimeToday: IRestaurantWorkTimeToday | undefined =
      restaurantWorkTimes[nowWeekDay];

    const getHourAndMinute = (date: string | undefined): { hours: number; minutes: number } => {
      //Не возвращаю null, т.к. моменты, где date придет как undefined отсекутся условиями
      if (!date) {
        return {
          hours: 0,
          minutes: 0,
        };
      }

      const [hours, minutes] = date.split(':').map(Number);
      return {
        hours,
        minutes,
      };
    };

    const startShiftTime = getHourAndMinute(restaurantWorkTimeToday?.start);
    const endShiftTime = getHourAndMinute(restaurantWorkTimeToday?.end);

    // Если выходной, то возвращаем 00:01 - 00:01, чтобы ничего не пришло
    const result = {
      from: startDay.clone().add(1, 'm').valueOf(),
      till: startDay.clone().add(1, 'm').valueOf(),
    };

    // Это выходной, нет ни предыдущей смены ночью, ни старта смены
    if (!restaurantWorkTimeToday?.previous && !restaurantWorkTimeToday?.start) {
      return result;
    }

    // Это выходной, но с предыдущей смены ночью, вернем с 00:00 до конца предыдущей смены
    if (restaurantWorkTimeToday?.previous && !restaurantWorkTimeToday?.start) {
      return {
        from: startDay.valueOf(),
        till: startDay.clone().add(getHourAndMinute(restaurantWorkTimeToday?.previous)).valueOf(),
      };
    }

    //Есть предыдущая смена - 00:00, нет - начало смены
    result.from = restaurantWorkTimeToday?.previous
      ? startDay.valueOf()
      : startDay.clone().add(getHourAndMinute(restaurantWorkTimeToday?.start)).valueOf();

    //Если начало после конца смены в 24-ч формате (12:00 и 05:00) то есть ночная смена
    const haveNightShift = startDay
      .clone()
      .add(startShiftTime)
      .isAfter(startDay.clone().add(endShiftTime));

    //Если ночная смена есть, то надо сдвинуть на один день вперед
    result.till = startDay
      .clone()
      .add(haveNightShift ? 1 : 0, 'd')
      .add(endShiftTime)
      .valueOf();

    return result;
  }, [propDate, restaurantWorkTimes]);
};
